import type { CatalogFact, CatalogOperator, OperatorId, RuleCondition, ValueInput } from '@/types/rules.types';

const LIST_INPUTS: ReadonlySet<ValueInput> = new Set(['texts', 'enums', 'intents', 'tags', 'emails']);

/**
 * How a condition's value is entered, from the catalog alone.
 *
 * A per-operator input wins over everything, including an operator that takes no value: "Kim checks
 * [question] is true" is a yes/no operator that still needs its question typed in. Otherwise an
 * operator without a value shows nothing, and the rest use the fact's own input.
 */
export function valueInputFor(fact: CatalogFact | undefined, op: OperatorId, operators: CatalogOperator[]): ValueInput {
	if (!fact) return 'none';
	const byOperator = fact.valueInputByOperator?.[op];
	if (byOperator) return byOperator;
	const operator = operators.find((candidate) => candidate.id === op);
	if (operator && !operator.needsValue) return 'none';
	return fact.valueInput;
}

export const isListInput = (input: ValueInput): boolean => LIST_INPUTS.has(input);

/** The empty value for an input: a list for list inputs, nothing for none, '' otherwise. */
export function emptyValueFor(input: ValueInput): unknown {
	if (input === 'none') return undefined;
	if (input === 'number') return undefined;
	return isListInput(input) ? [] : '';
}

/** True when the input asks for a value and none has been given. */
export function isValueMissing(input: ValueInput, value: unknown): boolean {
	if (input === 'none') return false;
	if (input === 'number') return typeof value !== 'number' || Number.isNaN(value);
	if (isListInput(input)) return !Array.isArray(value) || value.length === 0;
	return typeof value !== 'string' || value.trim() === '';
}

/** Whether a value already held fits an input, so a change of operator can keep it. */
function valueFits(input: ValueInput, value: unknown): boolean {
	if (input === 'none') return value === undefined;
	if (input === 'number') return typeof value === 'number';
	if (isListInput(input)) return Array.isArray(value);
	return typeof value === 'string';
}

/** A condition on a new fact: its first operator and an empty value. */
export function conditionForFact(condition: RuleCondition, fact: CatalogFact, operators: CatalogOperator[]): RuleCondition {
	const op = fact.operators[0] ?? condition.op;
	return { id: condition.id, fact: fact.id, op, value: emptyValueFor(valueInputFor(fact, op, operators)) };
}

/** A condition on a new operator, keeping the value when it still fits ("is any of" to "is none of"). */
export function conditionForOperator(
	condition: RuleCondition,
	fact: CatalogFact | undefined,
	op: OperatorId,
	operators: CatalogOperator[]
): RuleCondition {
	const input = valueInputFor(fact, op, operators);
	return { ...condition, op, value: valueFits(input, condition.value) ? condition.value : emptyValueFor(input) };
}
